import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import styled, { keyframes } from "styled-components";

import { ScrollChevron, ScrollHint, useScrollAffordance } from "@/components/CartModal/phoneTemplate";
import { useModalAnimation } from "@/hooks/useModalAnimation";
import { getAutoRelease, setAutoRelease } from "@/services/claimSubmissions.service";
import { colors, components, fonts, fontSizes } from "@/styles/design-system";

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(24px) scale(0.97); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
`;

const slideDown = keyframes`
  from { opacity: 1; transform: translateY(0) scale(1); }
  to   { opacity: 0; transform: translateY(24px) scale(0.97); }
`;

const Container = styled.div<{ $closing?: boolean }>`
  position: relative;
  width: 92%;
  max-width: 520px;
  /* dvh so the modal tracks the visible iOS Safari viewport (no edge clipping
     when the bottom bar appears). */
  max-height: 85dvh;
  display: flex;
  flex-direction: column;
  background: ${components.modal.background};
  border: ${components.modal.border};
  border-radius: ${components.modal.borderRadius};
  box-shadow: ${components.modal.boxShadow};
  overflow: hidden;
  animation: ${({ $closing }) => ($closing ? slideDown : slideUp)} 0.25s ease both;

  /* Phone: full width within the outer 16px padding; a touch more height. */
  @media (max-width: 600px) {
    width: 100%;
    max-height: 90dvh;
  }
`;

/* Fixed header (no ✕ — mandatory modal). Centred title + intro text. */
/* Standard modal header (matches CreditRecheckModal + the modal family): fixed,
   left-aligned, divider underneath. No ✕ and no right padding for one — this
   modal is mandatory, the choice has to be made. */
const ArHeader = styled.div`
  position: relative;
  flex-shrink: 0;
  padding: 22px 30px;
  border-bottom: 1px solid rgba(235, 194, 142, 0.16);
  @media (max-width: 600px) {
    padding: 18px 18px;
  }
`;

const ArTitle = styled.h2`
  font-family: ${fonts.heading};
  font-size: 1.9rem;
  font-weight: 600;
  line-height: 1.15;
  color: ${colors.text.heading};
  margin: 0;
  /* Phone only: centred. There is no ✕ to balance against on this modal, so a
     left-aligned title sits lopsided on a narrow screen. Desktop keeps the
     left alignment the rest of the modal family uses. */
  @media (max-width: 600px) {
    font-size: 1.55rem;
    text-align: center;
  }
`;

/* Scrollable choices — keeps the Apply button always visible below. */
const ArScrollBody = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 22px 36px 12px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  @media (max-width: 600px) {
    padding: 18px 22px 10px;
  }
`;

/* Fixed footer — Apply always visible. */
const ArFooter = styled.div`
  flex-shrink: 0;
  position: relative;
  display: flex;
  padding: 12px 36px 32px;
  @media (max-width: 600px) {
    padding: 12px 22px 24px;
  }
`;

const ArScrollFade = styled.div<{ $visible: boolean }>`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 100%;
  height: 48px;
  pointer-events: none;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding-bottom: 4px;
  background: linear-gradient(to bottom, rgba(89, 65, 46, 0) 0%, rgba(89, 65, 46, 0.82) 55%, rgba(89, 65, 46, 1) 100%);
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transition: opacity 0.25s ease;
  @media (min-width: 1150px) {
    display: none;
  }
`;

const ChoiceBtn = styled.button<{ $primary?: boolean; disabled?: boolean }>`
  all: unset;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  flex: 1;
  height: 44px;
  padding: 0 20px;
  border-radius: 12px;
  font-family: ${fonts.body};
  font-size: ${fontSizes.body};
  font-weight: 600;
  text-align: center;
  background: ${({ $primary }) =>
    $primary
      ? "linear-gradient(135deg, #9cd3fd, #6fb3ec)"
      : "rgba(235, 194, 142, 0.08)"};
  color: ${({ $primary }) => ($primary ? "#1b2742" : "#fff")};
  border: 1px solid
    ${({ $primary }) =>
      $primary ? "transparent" : "rgba(235, 194, 142, 0.25)"};
  transition: all 0.15s ease;
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    background: ${({ $primary }) =>
      $primary
        ? "linear-gradient(135deg, #b5dfff, #82c1f4)"
        : "rgba(235, 194, 142, 0.16)"};
  }
`;

/**
 * Mandatory modal that opens on /account/claims when a plan change has
 * force-reset the user's Auto Release preference. The user has to pick
 * one path before the modal closes. There's no overlay-close, no X, no
 * Escape handler — same pattern as PendingDowngradeGlobalModal.
 *
 * Backend contract:
 *   - GET /claims/auto-release returns autoReleaseChoiceRequired.
 *   - PUT /claims/auto-release (enabled: boolean) clears the flag.
 *
 * Re-opens automatically until the user picks. Polls on mount + on the
 * `auto-release-choice-changed` custom window event (fired after a plan
 * change is settled in Redux user state).
 */
const AutoReleaseChoiceModal: React.FC<{ devPreview?: boolean }> = ({ devPreview }) => {
  const [open, setOpen] = useState(false);
  const [readyCount, setReadyCount] = useState(0);
  // Default to "off" so the action button always carries a concrete
  // label ("Keep auto-resolve turned off") instead of a generic "Save"
  // and the user can commit with a single click.
  const [selected, setSelected] = useState<"on" | "off">("off");
  const [submitting, setSubmitting] = useState(false);
  const { mounted, closing } = useModalAnimation(devPreview ? true : open);
  const bodyRef = useRef<HTMLDivElement>(null);
  const canScrollDown = useScrollAffordance(bodyRef, [mounted]);

  const refetch = useCallback(async () => {
    if (devPreview) { setReadyCount(3); setOpen(true); return; }
    try {
      const state = await getAutoRelease();
      if (state.autoReleaseChoiceRequired) {
        setReadyCount(state.readyCount);
        setOpen(true);
      } else {
        setOpen(false);
      }
    } catch {
      setOpen(false);
    }
  }, [devPreview]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  useEffect(() => {
    const handler = () => refetch();
    window.addEventListener("auto-release-choice-changed", handler);
    window.addEventListener("focus", handler);
    return () => {
      window.removeEventListener("auto-release-choice-changed", handler);
      window.removeEventListener("focus", handler);
    };
  }, [refetch]);

  // Reset to the default "off" selection when the modal opens fresh.
  useEffect(() => {
    if (open) setSelected("off");
  }, [open]);

  const handleSave = useCallback(async () => {
    if (submitting) return;
    if (devPreview) { setOpen(false); return; }
    setSubmitting(true);
    try {
      // Flag only when turning ON — the claims list licenses the ready claims one
      // at a time as their cards animate, so the run is visible and stoppable.
      await setAutoRelease(selected === "on", selected === "on" ? { drain: false } : undefined);
      setOpen(false);
      // Tell the parent page (and any other listeners) that the auto-
      // resolve flag may have changed, so the toggle pill in the page
      // header refetches and reflects the new state.
      window.dispatchEvent(new Event("auto-release-choice-changed"));
      // Turning it ON releases every ready claim server-side, so run the SAME
      // per-card animation the normal toggle runs: each card walks Resolved! →
      // Moving → fade → collapse into Licensed, and LicensesTab refetches at the
      // end. Without this the modal just closed and the cards sat in Action
      // Required until a manual reload, which read as "the choice did nothing".
      if (selected === "on") {
        window.dispatchEvent(new CustomEvent("auto-release-animate-claims", { detail: { drive: true } }));
      }
    } catch (err) {
      console.error("Auto Release choice save failed:", err);
    } finally {
      setSubmitting(false);
    }
  }, [submitting, selected, devPreview]);

  if (!mounted) return null;

  return createPortal(
    <div
      style={{ position: "fixed", top: 0, left: 0, right: 0, height: "100dvh", zIndex: 1300, display: "flex", alignItems: "center", justifyContent: "center", padding: 16, boxSizing: "border-box" }}
    >
      <Container $closing={closing}>
        {/* Header — fixed, title only (the modal family's shape). */}
        <ArHeader>
          <ArTitle>How to resolve claims?</ArTitle>
        </ArHeader>

        {/* Radio-style choice rows (scrollable). Whole card is clickable. The
            selected card gets a gold ring + slightly brighter background. */}
        <ArScrollBody ref={bodyRef}>
          <p
            style={{
              fontFamily: fonts.body,
              fontSize: fontSizes.body,
              color: colors.text.body,
              lineHeight: 1.55,
              margin: 0,
            }}
          >
            Due to your configuration switch, we turned off the auto-resolve feature. How do you want Claims to be licensed?
          </p>
          {readyCount > 0 && (
            /* Pill, at the intro's own size — the count is the fact that decides
               the choice, so it reads as a statement rather than fine print. */
            <span
              style={{
                alignSelf: "flex-start",
                // inline-BLOCK, not inline-flex: as a flex row each fragment of
                // the sentence became its own item, so on a phone it broke into
                // columns with the number stranded in the middle. Normal text
                // flow wraps it as one sentence.
                display: "inline-block",
                padding: "6px 12px",
                borderRadius: 6,
                background: "rgba(235, 194, 142, 0.12)",
                border: "1px solid rgba(235, 194, 142, 0.22)",
                fontFamily: fonts.body,
                fontSize: fontSizes.body,
                color: colors.text.body,
                lineHeight: 1.45,
                marginBottom: 8,
              }}
            >
              You currently have{" "}
              <strong style={{ fontFamily: fonts.number, color: colors.text.primary }}>
                {readyCount}
              </strong>{" "}
              claim{readyCount === 1 ? "" : "s"} ready to be resolved.
            </span>
          )}
          {([
            { key: "on", title: "Turn on auto-resolve", body: "Claims that are ready get licensed automatically until your monthly limits are reached. Applies to existing and new claims." },
            { key: "off", title: "Manual review (no auto-resolve)", body: "Claims wait in the “Action Required” tab until you click the “License” button. Better control over which videos consume monthly credits, but also more manual work." },
          ] as const).map((opt) => {
            const isSelected = selected === opt.key;
            return (
              <button
                key={opt.key}
                type="button"
                onClick={() => setSelected(opt.key)}
                disabled={submitting}
                style={{
                  all: "unset",
                  cursor: submitting ? "default" : "pointer",
                  display: "flex",
                  gap: 14,
                  alignItems: "flex-start",
                  padding: "14px 16px",
                  borderRadius: 14,
                  background: isSelected ? "rgba(156, 211, 253, 0.12)" : "rgba(235, 194, 142, 0.04)",
                  border: `1px solid ${isSelected ? "rgba(156, 211, 253, 0.6)" : "rgba(235, 194, 142, 0.18)"}`,
                  transition: "background 0.15s ease, border-color 0.15s ease",
                }}
              >
                {/* Radio indicator */}
                <span
                  style={{
                    flexShrink: 0,
                    width: 18,
                    height: 18,
                    marginTop: 2,
                    borderRadius: "50%",
                    border: `2px solid ${isSelected ? "#9cd3fd" : "rgba(235, 194, 142, 0.45)"}`,
                    background: "transparent",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {isSelected && (
                    <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#9cd3fd" }} />
                  )}
                </span>
                <div style={{ flex: 1, textAlign: "left" }}>
                  <div style={{ fontFamily: fonts.body, fontSize: fontSizes.body, fontWeight: 600, color: "#fff", marginBottom: 4 }}>
                    {opt.title}
                  </div>
                  <div style={{ fontFamily: fonts.body, fontSize: fontSizes.sm, color: "rgba(251, 244, 236, 0.65)", lineHeight: 1.5 }}>
                    {opt.body}
                  </div>
                </div>
              </button>
            );
          })}
        </ArScrollBody>

        {/* Fixed footer — single action button (always visible). */}
        <ArFooter>
          <ArScrollFade $visible={canScrollDown} aria-hidden="true"><ScrollHint><span>Scroll</span><ScrollChevron /></ScrollHint></ArScrollFade>
          <ChoiceBtn
            $primary
            onClick={handleSave}
            disabled={submitting}
          >
            {submitting ? "Applying…" : "Apply"}
          </ChoiceBtn>
        </ArFooter>
      </Container>
    </div>,
    document.body,
  );
};

export default AutoReleaseChoiceModal;
